// Sticky visitor identity: UUID persisted to localStorage with cookie mirror
// for cross-subdomain reuse.

const STORAGE_KEY = '_ab_vid';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older environments
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readFromLocalStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function readFromCookie(): string | null {
  try {
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${STORAGE_KEY}=`));
    return match ? decodeURIComponent(match.slice(STORAGE_KEY.length + 1)) : null;
  } catch {
    return null;
  }
}

function writeToLocalStorage(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // storage may be disabled
  }
}

function writeToCookie(id: string, domain?: string): void {
  try {
    let cookie = `${STORAGE_KEY}=${encodeURIComponent(id)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
    if (domain) cookie += `; domain=${domain}`;
    document.cookie = cookie;
  } catch {
    // cookies may be disabled
  }
}

/**
 * Get or create a sticky visitor ID.
 *
 * localStorage is the primary store. The cookie mirrors it so the same
 * ID survives across subdomains when `domain` is provided.
 *
 * Returns null in non-browser environments.
 */
export function getOrCreateVisitorId(domain?: string): string | null {
  if (typeof window === 'undefined') return null;

  const existing = readFromLocalStorage() ?? readFromCookie();
  if (existing) {
    // Ensure both stores are populated (one may be missing if cleared)
    writeToLocalStorage(existing);
    writeToCookie(existing, domain);
    return existing;
  }

  const id = generateUUID();
  writeToLocalStorage(id);
  writeToCookie(id, domain);
  return id;
}

/**
 * Clear the stored visitor ID from both localStorage and the cookie.
 * Useful for logout / reset flows.
 */
export function clearVisitorId(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    document.cookie = `${STORAGE_KEY}=; max-age=0; path=/`;
  } catch {
    // ignore
  }
}

export { STORAGE_KEY };
